import React, { useState, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { getSites, getAssets, getReports, getStores } from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Download, Printer } from 'lucide-react';

// Same logo asset the rest of the app pulls from Wix CDN. Crossorigin
// is needed so the browser can include it cleanly when the page is
// rendered to a printer; the source already returns a permissive CORS
// header so this works without a proxy.
const LOGO_URL = 'https://static.wixstatic.com/media/47e68b_9ba20c13e75f4752b5585a413624d1cc~mv2.png/v1/fill/w_342,h_72,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Icon%20%2B%20Lutrom%20%2B%20Engineering.png';

const QrPrintCard = ({ url, title, subtitle, code, footer }) => (
  <div data-qr-card className="bg-white border-2 border-slate-900 rounded-md p-5 flex flex-col items-center text-center">
    <div className="w-full pb-3 border-b-2 border-slate-900 mb-4 flex items-center justify-center">
      <img
        src={LOGO_URL}
        alt="Lutrom Engineering"
        crossOrigin="anonymous"
        className="h-10 w-auto"
      />
    </div>
    <div className="bg-white p-2 border border-slate-200 rounded"><QRCodeCanvas value={url} size={160} level="M" includeMargin={false} /></div>
    <div className="mt-3 text-xs font-mono text-slate-500">{code}</div>
    <div className="mt-1 font-semibold text-slate-900 text-sm">{title}</div>
    {subtitle && <div className="text-xs text-slate-600 mt-0.5">{subtitle}</div>}
    <div className="mt-2 text-[10px] text-slate-500 uppercase tracking-wider">{footer || 'Scan to report an issue'}</div>
  </div>
);

const QrCodes = () => {
  const [tab, setTab] = useState('sites');
  const [sites, setSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [reports, setReports] = useState([]);
  const [stores, setStores] = useState([]);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  useEffect(() => {
    Promise.all([getSites(), getAssets(), getReports(), getStores()])
      .then(([s, a, r, st]) => { setSites(s); setAssets(a); setReports(r); setStores(st); });
  }, []);

  const print = () => window.print();
  const downloadCanvas = (id, name) => {
    const canvas = document.querySelector(`#qr-${id} canvas`);
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `lutrom-qr-${name}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 qr-print-region">
      <div className="qr-print-hide">
        <PageHeader title="QR Codes" description="Generate and print QR codes for sites, assets and reports."
          actions={<Button variant="outline" onClick={print}><Printer className="h-4 w-4 mr-2" /> Print Page</Button>} />
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="qr-print-hide">
          <TabsTrigger value="sites">Sites</TabsTrigger>
          <TabsTrigger value="assets">Assets</TabsTrigger>
          <TabsTrigger value="stores">Stores</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>
        <TabsContent value="sites" className="mt-6">
          <p className="text-sm text-slate-600 mb-4 qr-print-hide">Print and post these QR codes at the site entrance — anyone can scan to submit a work request.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 qr-print-grid">
            {sites.map(s => {
              const url = `${origin}/qr/request/${s.id}`;
              return (
                <div key={s.id} id={`qr-${s.id}`} className="space-y-2">
                  <QrPrintCard url={url} title={s.name} subtitle={s.address} code={s.id} />
                  <Button size="sm" variant="outline" className="w-full qr-print-hide" onClick={() => downloadCanvas(s.id, s.id)}><Download className="h-3.5 w-3.5 mr-1" /> Download PNG</Button>
                </div>
              );
            })}
          </div>
        </TabsContent>
        <TabsContent value="assets" className="mt-6">
          <p className="text-sm text-slate-600 mb-4 qr-print-hide">Stick these on each asset. Scanning auto-tags the request to the asset.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 qr-print-grid">
            {assets.map(a => {
              const url = `${origin}/qr/request/${a.siteId || 'SITE-ACME'}?asset=${a.id}`;
              return (
                <div key={a.id} id={`qr-${a.id}`} className="space-y-2">
                  <QrPrintCard url={url} title={a.name} subtitle={a.location} code={a.id} />
                  <Button size="sm" variant="outline" className="w-full qr-print-hide" onClick={() => downloadCanvas(a.id, a.id)}><Download className="h-3.5 w-3.5 mr-1" /> Download PNG</Button>
                </div>
              );
            })}
          </div>
        </TabsContent>
        <TabsContent value="stores" className="mt-6">
          <p className="text-sm text-slate-600 mb-4 qr-print-hide">Stick these on each spares store door. Operators scan to take parts — no login required, every pickup is logged.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 qr-print-grid">
            {stores.map(s => {
              const url = `${origin}/qr/store/${s.id}`;
              const subtitle = s.kind === 'customer' ? 'Customer-owned · ' : 'Lutrom-owned · ';
              return (
                <div key={s.id} id={`qr-${s.id}`} className="space-y-2">
                  <QrPrintCard
                    url={url}
                    title={s.name}
                    subtitle={`${subtitle}${s.address || ''}`}
                    code={s.id}
                    footer="Scan to take parts"
                  />
                  <Button size="sm" variant="outline" className="w-full qr-print-hide" onClick={() => downloadCanvas(s.id, s.id)}><Download className="h-3.5 w-3.5 mr-1" /> Download PNG</Button>
                </div>
              );
            })}
            {stores.length === 0 && (
              <div className="col-span-full p-12 text-center text-slate-500 bg-white border border-dashed border-slate-200 rounded-md qr-print-hide">
                No stores yet. Create one on the Stores page first.
              </div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="reports" className="mt-6">
          <p className="text-sm text-slate-600 mb-4 qr-print-hide">Attach these to printed reports. Customers can scan to view the full digital report.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 qr-print-grid">
            {reports.map(r => {
              const url = `${origin}/report/${r.id}`;
              return (
                <div key={r.id} id={`qr-${r.id}`} className="space-y-2">
                  <QrPrintCard url={url} title={r.type} subtitle={`Issued ${r.issuedAt}`} code={r.id} />
                  <Button size="sm" variant="outline" className="w-full qr-print-hide" onClick={() => downloadCanvas(r.id, r.id)}><Download className="h-3.5 w-3.5 mr-1" /> Download PNG</Button>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default QrCodes;
